import 'projects/sharedComponents/src/breedSummary/breedSummaryCard.scss';

import { Box, Button, Card, Grid, Typography } from '@mui/material';
import {
  BreedSummaryCardFragment,
  CareResourcesReasonCategory,
} from 'projects/sharedComponents/src/generated/graphql-client-types';
import { ProductTypePill, productTypeToLabelMap } from 'projects/sharedComponents/src/pet/productType/productTypePill';

import { AccountDetails } from 'clientServerShared/types/account';
import { BreedHalo } from 'projects/sharedComponents/src/dogAvatar/breedHalo';
import { BreedSummaryBars } from 'projects/sharedComponents/src/breedSummary/breedSummaryBars';
import { CareResourcesResultsBanner } from 'projects/sharedComponents/src/pet/careResources/resultsBanner/careResourcesResultsBanner';
import { FC } from 'react';
import { Heap } from 'clientServerShared/types/eventTracking';
import { RenderIf } from 'projects/sharedComponents/src/renderStates/renderIf';
import { ShareButton } from 'projects/sharedComponents/src/pet/shareModal/shareButton';
import { buildMyEvLegacyLink } from 'projects/sharedComponents/src/services/links/linkBuilder';
import { shorten } from 'projects/sharedComponents/src/services/stringFormatters/stringFormatters';

type BreedSummaryCardProps = {
  dog: BreedSummaryCardFragment;
  showProductType?: boolean;
  showShare?: boolean;
  shouldShowViewButtons?: boolean;
  showCareResourcesBanner?: boolean;
  accountDetails: AccountDetails;
} & Heap;

export const BreedSummaryCard: FC<BreedSummaryCardProps> = ({
  dog,
  showProductType = true,
  showShare = false,
  shouldShowViewButtons = false,
  accountDetails,
  showCareResourcesBanner = false,
  heapMetadata,
}) => {
  const dogName = shorten(dog.name, 25);

  const isBreeder = accountDetails?.user?.permissions.isBreeder;
  if (isBreeder) {
    showProductType = false;
  }

  const isVet = accountDetails?.user?.permissions.isVet;
  let viewBreedButtonText = 'View Breed & Ancestry';
  if (isVet) {
    viewBreedButtonText = 'View Breed';
  }

  const countryCode = accountDetails.user?.countryCode ?? 'US';

  return (
    <Card className="breedSummaryCard">
      <Grid container spacing={3} className="breedSummaryCard-clickableContent">
        <Grid item container className="breedSummaryCard-haloContainer" sm={3} direction="column" alignItems="center">
          <Grid item>
            <BreedHalo dog={dog} width={138} />
          </Grid>
          <Grid item>
            {!!dog.swabSummary.productType && showProductType && (
              <ProductTypePill productLabel={productTypeToLabelMap[dog.swabSummary.productType]} />
            )}
          </Grid>
          <Grid item>
            {!!dog.shareOptions && showShare && (
              <ShareButton shareOptions={dog.shareOptions} heapMetadata={heapMetadata} />
            )}
          </Grid>
        </Grid>
        <Grid className="breedSummaryCard-dogDetails" container item direction="column" sm={9} spacing={1}>
          <Grid item container>
            <Grid item sm={6}>
              <Typography variant="h5" component="p">
                <strong>{dogName}</strong>
              </Typography>
            </Grid>
          </Grid>
          <Grid item>
            <Typography>
              <strong>{dog.genotype?.breedType}</strong>
            </Typography>
          </Grid>
          <Grid item className="breedSummaryCard-breedBars-container">
            <BreedSummaryBars dog={dog} />
          </Grid>
        </Grid>
        <RenderIf condition={shouldShowViewButtons}>
          <Grid item xs={12} sm={12} className="breedSummaryCard-viewButtons">
            <Button
              className="breedSummaryCard-viewBreedResults"
              variant="contained"
              color="primary"
              href={buildMyEvLegacyLink(`/members/results/breed?i=${dog.petNum}&countryCode=${countryCode}`)}
            >
              {viewBreedButtonText}
            </Button>
            <Button
              className="breedSummaryCard-viewProfile"
              variant="outlined"
              color="secondary"
              href={buildMyEvLegacyLink(`/members/results/profile?i=${dog.petNum}`)}
            >
              View Profile
            </Button>
          </Grid>
        </RenderIf>
      </Grid>
      <RenderIf condition={showCareResourcesBanner}>
        <Box sx={{ paddingTop: { xs: 2, sm: 1 } }}>
          <CareResourcesResultsBanner pet={dog} category={CareResourcesReasonCategory.Breed} />
        </Box>
      </RenderIf>
    </Card>
  );
};
